import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type TeamOpenToInvitationJSON = z.infer<typeof TeamOpenToInvitationSchema.TeamOpenToInvitationJSONZ>;
export type TeamOpenToInvitationParams = z.infer<typeof TeamOpenToInvitationSchema.TeamOpenToInvitationZ>;

/**
 * One team the signed-in viewer may put somebody onto.
 *
 * Narrower than a `Team` on purpose: the API answers the Invite to Team picker with a name, a face and
 * whether the person is already on the team, and nothing about who else is. Parsing it as a `Team`
 * would invent a roster the server never sent.
 */
export default class TeamOpenToInvitationSchema {
  public static readonly TeamOpenToInvitationJSONZ = z.object({
    id: z.uuid(),
    name: z.string(),
    avatarId: z.uuid().nullish(),
    inviteeAlreadyBelongs: z.boolean(),
  });

  public static readonly TeamOpenToInvitationZ = z.object({
    id: z.instanceof(Guid),
    name: z.string(),
    avatarId: z.instanceof(Guid).optional(),
    inviteeAlreadyBelongs: z.boolean(),
  });

  public static validate(values: unknown): TeamOpenToInvitationParams {
    return parseOrThrow(TeamOpenToInvitationSchema.TeamOpenToInvitationZ, values, {
      logError: true,
      schemaName: "TeamOpenToInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): TeamOpenToInvitationJSON {
    return parseOrThrow(TeamOpenToInvitationSchema.TeamOpenToInvitationJSONZ, json, {
      logError: true,
      schemaName: "TeamOpenToInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is TeamOpenToInvitationParams {
    try {
      TeamOpenToInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is TeamOpenToInvitationJSON {
    try {
      TeamOpenToInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: TeamOpenToInvitationJSON): TeamOpenToInvitationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(TeamOpenToInvitationSchema.TeamOpenToInvitationJSONZ, json, {
      logError: true,
      schemaName: "TeamOpenToInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return TeamOpenToInvitationSchema.codec.decode(parsed);
  }

  public static serialize(params: TeamOpenToInvitationParams): TeamOpenToInvitationJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = TeamOpenToInvitationSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return TeamOpenToInvitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    TeamOpenToInvitationSchema.TeamOpenToInvitationJSONZ,
    TeamOpenToInvitationSchema.TeamOpenToInvitationZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        name: data.name,
        avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
        inviteeAlreadyBelongs: data.inviteeAlreadyBelongs,
      }),
      encode: (team) => ({
        id: team.id.toJSON(),
        name: team.name,
        avatarId: isNullOrUndefined(team.avatarId) ? null : team.avatarId.toJSON(),
        inviteeAlreadyBelongs: team.inviteeAlreadyBelongs,
      }),
    }
  );
}
